import { Component, OnInit, OnDestroy, HostListener, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DatacontextService } from '../../../data/datacontext.service';
import { AppSelectorService } from '../../../shared/services/app-selector/app-selector.service';
import { EnvironmentManagerService } from '../../../shared/services/environment-manager/environment-manager.service';
import { FeatureListJsonService } from 'src/app/shared/services/config-manager/feature-list/feature-list-json.service';
import { SettingsService } from 'src/app/data/repositories/settings/settings.service';
import { CreateAppComponent } from 'src/app/shared/components/create-app/create-app.component';
import { SidebarService } from 'src/app/core/sidebar/sidebar.service';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-selector',
  templateUrl: './app-selector.component.html',
  styleUrls: ['./app-selector.component.scss']
})
export class AppSelectorComponent implements OnInit, OnDestroy {
  appMap: any = {
    production: [],
    stage: []
  };
  currentEnvironment: string;
  selectedApp: any;
  public shouldShow = null;
  private createAppDialogRef: MatDialogRef<CreateAppComponent>;
  isOpen: boolean;
  searchText: string;
  disableDropDown = false;
  private subs = new SubSink();

  constructor(private appSelectorService: AppSelectorService,
    private dialog: MatDialog,
    private datacontextService: DatacontextService,
    private environmentManagerService: EnvironmentManagerService,
    private _route: ActivatedRoute,
    private featureListJsonService: FeatureListJsonService,
    private settingsService: SettingsService,
    private _router: Router,
    private eRef: ElementRef,
    private sidebarService: SidebarService) {
      this.subs.sink = this.environmentManagerService.getEnvironmentChangedListener().subscribe(env => {
      this.boot();
    });

    this.subs.sink = this.appSelectorService.getAppsLoaded().subscribe((appId: string) => {
      this.boot(appId);
    });
    this.subs.sink = this.appSelectorService.getAppChangedListener().subscribe(() => {
      this.shouldShow = null;
      this.shouldShowThisSection();
    });
    this.subs.sink = this.sidebarService.appSelectorListener().subscribe(() => {
      this.toggleDropdown();
    })
  }

  ngOnInit() {
    this.watchRouteChange();
    this.isOpen = false;
    this.selectedApp = {
      app: undefined,
      index: -1
    };
    this.shouldShowThisSection();
  }

  shouldShowThisSection() {
    if (this.shouldShow !== null) {
      return this.shouldShow;
    }
    this.featureListJsonService.shouldEnableTheFeature('hide_sandbox', false, flag => this.shouldShow = flag);
  }

  private boot(appId?: string) {
    this.currentEnvironment = this.environmentManagerService.getCurrentEnvironment();
    this.setupApps(appId);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  checkClicks(event: any) {
    if (!this.eRef.nativeElement.contains(event.target) && !event.target.classList.contains('sidebar-platform-icon')) {
      this.closeDropdown();
    }
  }

  public openDropdown() {
    this.isOpen = true;
  }

  public closeDropdown() {
    this.isOpen = false;
  }

  public toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  selectApp(app: any, idx: number, stage = null) {
    this.closeDropdown();
    if (!this.selectedApp) {
      this.selectedApp = {
        app: app,
        index: idx
      };
    }
    // if (this.selectedApp.app === app) {
    //   return;
    // }
    this.selectedApp.app = app;
    this.selectedApp.index = idx;
    this.appSelectorService.setSelectedApp(app);
    if (app && app.id) {
      this.settingsService.getAppVersionDetails({ 'app_id': app.id, 'list_all': true }).subscribe(data => {
        if (data && !data['is_error'] && data['api_response'] && data['api_response'].hasOwnProperty('app_versions')) {
          this.settingsService.setAppAndSDKVersions(data['api_response']['app_versions']);
        }
      });
    }
    this.searchText = '';
  }

  showSelectedApp(): boolean {
    let flag = false;
    if (this.searchText && this.selectedApp.app.name.toLowerCase().includes(this.searchText)) {
      flag = true;
    }
    if (this.selectedApp.app && this.selectedApp.app.id && !this.searchText) {
      flag = true;
    }
    return flag;
  }

  private setupApps(appId?: string) {
    this.appMap.production = this.appSelectorService.getApplist('production');
    this.appMap.stage = this.appSelectorService.getApplist('stage');

    if (appId) {
      this.selectAppWithAppId(appId);
    } else {
      this.setFirstSelectedApp();
    }
  }

  getAppListByAppState(state) {
    return this.appSelectorService.getApplist(this.currentEnvironment).filter(app => app.app_state === state);
  }

  setFirstSelectedApp() {
    this.selectApp(this.appSelectorService.getSelectedApp(), 0);
  }

  getIdForEnvironment(appId: string, environment: string) {
    let idx = this.appMap[environment].findIndex((app: any) => {
      return app.id === appId;
    });
    return idx;
  }

  selectAppWithAppId(appId: string) {
    let idx = this.getIdForEnvironment(appId, this.currentEnvironment);
    this.selectApp(this.appMap[this.currentEnvironment][idx], idx);
  }

  watchRouteChange() {
    this._router.events.subscribe((event) => {
      if (event['url']) {
        const currentUrl = event['url'];
        this.disableDropDown = currentUrl.includes('context-builder/feature-management/journey-builder') ||
          currentUrl.includes('context-builder/nudge-journeys/journey-builder') ||
          currentUrl.includes('context-builder/configure');
      }
    });
  }

  openCreateAppForm() {
    this.createAppDialogRef = this.dialog.open(CreateAppComponent, {
      panelClass: 'full-screen-dialog-container',
      disableClose: true
    });

    this.createAppDialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this._router.navigate(['/settings']);
        this.appSelectorService.loadApps(null, () => {
          const env = this.environmentManagerService.getCurrentEnvironment();
          const appList = this.appSelectorService.getApplist(env);
          let createdApp = {};
          appList.forEach(app => {
            if (app.id === result.api_response.id) {
              createdApp = app;
            }
          });
          this.appSelectorService.setSelectedApp(createdApp);
          this._router.navigate(['app-settings'], { queryParams: { 'new-app': true } });
        });
      }
    })
  }
}
